import {
  InfiniteData,
  RefetchOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { UseMutationResult } from "@tanstack/react-query";
import { z } from "zod";
import { WithPagination } from "./general.types";

// Define tier type for community token tiers
export interface Tier {
  id: string;
  name: string;
  requiredAmount: number;
  benefits: string[];
  color: string;
}

// Default tier for fallback
export const DEFAULT_TIER: Tier = {
  id: "bronze",
  name: "Bronze",
  requiredAmount: 0,
  benefits: ["Base rewards"],
  color: "#CD7F32",
};

export type TaskMutationParams = {
  campaignId: string;
  taskGuid: string;
  taskName: string;
  groupId: string | undefined;
  extraArguments?: string[];
};

export enum ChallengesCampaignType {
  CHALLENGE = "CHALLENGE",
  AIRDROP = "AIRDROP",
  XLAUNCHPAD = "XLAUNCHPAD",
}

export enum CampaignType {
  LAUNCHPAD = "launchpad",
  AIRDROP = "airdrop",
  CHALLENGE = "challenge",
}

export interface TaskArgument {
  name: string;
  value: string;
}

export interface TaskMedia {
  lightModeImageUrl: string;
  darkModeImageUrl: string;
}

export interface ChallengeTask {
  id: string;
  type: string;
  title: string;
  description: string;
  taskName: string;
  points: string;
  retryTime: number;
  media: {
    lightModeImageUrl: string;
    darkModeImageUrl: string;
  };
  boosterIncrease?: string;
  arguments: any[];
  displayOrder: number;
  isEnabled?: boolean;
}

export interface QuestEligibility {
  isEligible: boolean;
  requirements: {
    inTopX?: boolean;
    onchain?: boolean;
    social?: boolean;
    points?: boolean;
  };
}

export interface QuestDistributionInfo {
  topCount: number;
  distributionType: "TOPX" | "RANDOM" | "TIERS" | "EVEN";
  requirements: {
    social?: number;
    onchain?: number;
    points?: number;
  };
  tiers?: {
    [range: string]: string; // e.g. "1": "1000", "2-9": "500"
  };
  address?: string;
  chainId?: string;
}

export interface ChallengeCampaign {
  id: string;
  ownerAddress: string;
  startDate: Date;
  campaignGroup: string | undefined;
  endDate: Date;
  isEnabled: boolean;
  metadata: {
    referralTiers: {
      [key: string]: string;
    };
    distributionInfo: QuestDistributionInfo;
    [key: string]: any;
    isWeeklyLeaderboardEnabled?: boolean;
  };
  chainId: string;
  type: ChallengesCampaignType;
  tasks?: ChallengeTask[];
  isReferralEnabled?: boolean;
}

// DTOs
export const taskArgumentSchema = z.object({
  name: z.string(),
  value: z.string(),
});

export const taskMediaSchema = z.object({
  lightModeImageUrl: z.string(),
  darkModeImageUrl: z.string(),
});

export const taskSchema = z.object({
  type: z.string(),
  title: z.string(),
  description: z.string(),
  taskName: z.string(),
  points: z.string(),
  retryTime: z.number(),
  media: taskMediaSchema,
  boosterIncrease: z.string().optional(),
  arguments: z.record(z.string(), z.any()).default({}),
  displayOrder: z.number(),
  isEnabled: z.boolean().optional(),
});

export const campaignSchema = z.object({
  id: z.string(),
  ownerAddress: z.string().optional(),
  startDate: z.date(),
  endDate: z.date(),
  isEnabled: z.boolean(),
  metadata: z.record(z.string(), z.any()),
  type: z.nativeEnum(ChallengesCampaignType),
  tasks: z.array(taskSchema).optional(),
});

export type CreateTaskDto = z.infer<typeof taskSchema>;
export type CreateCampaignDto = z.infer<typeof campaignSchema>;
export type UpdateCampaignDto = Partial<CreateCampaignDto>;

export interface ChallengeUser {
  address: string;
  campaignId: string;
  booster: number;
  taskPoints: string;
  classPoints: string;
  totalPoints: string;
  referralPoints: string;
  rank: number;
  name?: string;
  photoUrl?: string;
  createdVestingDate?: Date;
  tokens?: string;
  isVerified?: boolean;
  hasPremiumSubscription?: boolean;
  eligibility?: QuestEligibility;
  scamInfo: any[] | null;
  email?: string;
  tier?: {
    description: string;
    id: string;
    image: string;
    name: string;
    tierLowerBound: number;
    tillNextTier: number;
    tillNextTierPercentage: number | null | undefined;
    tokens: string;
  };
  claimInfo: {
    tokensToClaim: string;
    tokensClaimedAt: Date | null;
    claimTxHash: string | null;
  };
  referralData: {
    code: string;
    pointsObtained: string;
    pointsGiven: string;
    referredBy: string | null;
  };
}

export const emptyChallengeUser = (campaignId: string): ChallengeUser => {
  const emptyUser = {
    address: "",
    campaignId: campaignId,
    booster: 0,
    taskPoints: "0",
    classPoints: "0",
    totalPoints: "0",
    referralPoints: "0",
    rank: 99999,
    claimInfo: {
      tokensToClaim: "0",
      tokensClaimedAt: null,
      claimTxHash: null,
    },
    referralData: {
      code: "",
      pointsObtained: "0",
      pointsGiven: "0",
      referredBy: null,
    },
    scamInfo: null,
  };

  return emptyUser;
};

export enum ChallengeCampaignEventState {
  SUCCESSFUL = "SUCCESSFUL",
  PENDING = "PENDING",
  ERROR = "ERROR",
}

export interface ChallengeTaskMedia {
  lightModeImageUrl: string;
  darkModeImageUrl: string;
}

export interface TaskStatus {
  taskGuid: string;
  taskName: string;
  pointsReceived: string;
  maxPoints: string;
  status: ChallengeCampaignEventState;
  cooldown: number;
  // this is a timestamp
  cooldownEndsAt: number;
  title: string;
  description: string;
  media: ChallengeTaskMedia;
  extraArguments: string[];
  type: string;
  isEnabled: boolean;
}
export interface ChallengeCampaignStatus {
  tasksStatus: TaskStatus[];
  classesStatus: ChallengeUserClassInfo[];
}

export interface ImageType {
  src: string;
  alt: string;
  width: number;
  height: number;
}

export interface ClassMetadata {
  title: string;
  icon: string;
  description: string;
  images: ImageType[];
  shortDescription: string;
}

export interface ChallengeMetadata {
  /** @deprecated Use name instead */
  title: string;
  name: string;
  description: string;
  descriptionShort: string;
  image: string;
  banner: string;
  bannerMobile: string;
  projectImage: string;
  totalAmount: string;
  opengraphBanner: string;
  projectMetadata: ClassMetadata;
  partnersMetadata: ClassMetadata;
  ecosystemMetadata: ClassMetadata;
  projectDescription: string;
  projectName: string;
  primaryColor: string;
  tokenLogo: string;
  decimals: number;
  taskGroupsImages: {
    [key: string]: string;
  };
  taskGroupsColors: {
    [key: string]: string;
  };
  tokenSymbol?: string;
  token?: string;
  distributionAddress?: string;
  verifiedMultiplier?: number;
  referralTiers?: {
    [key: string]: string;
  };
  boosters?: {
    name: string;
    multiplier: number;
    description: string;
    image: ImageType;
  }[];
  distributionInfo?: QuestDistributionInfo;
  parachuteImg: string;
  airdropVisual: string;
  phase1Text: string;
  phase2Text: string;
  phase3Text: string;
  projectPageUrl: string;
  eligibilityTicketsText: string;
  eligibilityDetailsText: string;
  ticketImage: string;
  launchpadBanner: string;
}

export interface ChallengeUserClassInfo {
  points: string;
  classId: string;
}

export type ChallengeTaskAndArgs = {
  guid: string;
  id: string;
  points: string;
  retryTime: number;
  args: TaskArgument[];
  title: string;
  description: string;
  image: ChallengeTaskMedia;
  boosterIncrease: number;
  isEnabled: boolean;
};

export type ChallengeMarkdown = {
  lightModeMarkdown: string;
  darkModeMarkdown: string;
};

export interface IChallengeClass {
  id: string;
  name: string;
  medias: ChallengeTaskMedia[];
  markdown: ChallengeMarkdown;
}

export interface ChallengeCampaignWithTotal extends ChallengeCampaign {
  totalPoints: string;
}

export type CompleteTaskMutation = UseMutationResult<
  {
    status: boolean;
    error?: string | undefined;
  } | null,
  Error,
  TaskMutationParams,
  unknown
>;

export type ChallengeLeaderboardQuery = UseInfiniteQueryResult<
  InfiniteData<WithPagination<ChallengeUser>, unknown>,
  Error
>;

export type GroupedTasks = TaskStatus[][];

export interface CampaignTopUser {
  address: string;
  points: string;
  rank: number;
  name?: string;
  imageUrl?: string;
}

export interface Campaign {
  id: string;
  campaignGroup?: string;
  type?: CampaignType;
  name: string;
  description: string;
  imageUrl: string;
  startDate: string | Date;
  endDate: string | Date;
  allocation: string;
  token: string;
  totalUsers: number;
  topUsers: CampaignTopUser[];
  chainId: string;
  totalQuests: number;
  isEnabled?: boolean;
  tokenImageUrl: string;
  tokenSymbol: string;
}

export type GetAllCampaignsResponse = Campaign[];

export type UseQuestCheckin = ({
  contractAddress,
}: {
  contractAddress: string;
}) => {
  checkInFee: bigint | undefined;
  hasCheckedIn: boolean | undefined;
  refetchCheckinStatus: (options?: RefetchOptions) => Promise<void>;
  isCheckinFeeLoading: boolean;
  isCheckinStatusLoading: boolean;
};

export type DailyCheckinMutation = UseMutationResult<
  void,
  Error,
  {
    contractAddress: string;
    fee: bigint;
  },
  unknown
>;

export type UseGetAccountTokens = () => UseQueryResult<
  {
    denominatedBalance: string;
    balance: string;
    symbol: string;
    address: string;
    name: string;
    decimals: number;
    chainId: string;
    logoUri: string;
    price?: number;
    totalUsdValue: number;
  }[],
  Error
>;

export enum TrendingPeriod {
  DAY = "day",
  WEEK = "week",
}

export enum LeaderboardPeriodType {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  YEARLY = "yearly",
  ALL = "all",
}

export interface Highlight {
  id: string;
  title: string;
  description: string;
  image: string;
  link: string;
}

// Highlight types for community updates
export interface TwitterHighlight extends Highlight {
  platform: "twitter";
  tweetId: string;
  stats: {
    likes: number;
    comments?: number;
    retweets?: number;
  };
}

export interface DiscordHighlight extends Highlight {
  platform: "discord";
  channelId: string;
  messageId: string;
  serverId: string;
  serverName?: string;
}
